import {
  pollForAccessToken,
  startPasswordlessLogin,
} from '@paytrie/common/auth'

type PasswordlessClient = Parameters<typeof startPasswordlessLogin>[0]['client']
import * as Sentry from '@sentry/react'
import { useCallback, useEffect, useRef, useState } from 'react'

type PollErrorKind = 'timed-out' | 'unexpected'

type VerifiedPayload = {
  email: string
  accessToken: string
}

type Options = {
  client: PasswordlessClient
  sentryComponent: string
  errorMessages: {
    sendFailed: string
    timedOut: string
    unexpected: string
  }
  onVerified: (payload: VerifiedPayload) => void
}

type SendArgs = {
  email: string
  referral?: string
  vendor?: string
}

export const usePasswordlessAuth = ({
  client,
  sentryComponent,
  errorMessages,
  onVerified,
}: Options) => {
  const [waitingForApproval, setWaitingForApproval] = useState(false)
  const [denied, setDenied] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [lastSendSucceeded, setLastSendSucceeded] = useState(true)

  const pollAbortRef = useRef<AbortController | null>(null)
  const activeSessionRef = useRef<{
    sessionId: string
    email: string
  } | null>(null)

  useEffect(() => () => pollAbortRef.current?.abort(), [])

  const beginPoll = useCallback(
    (session: { sessionId: string; email: string }) => {
      pollAbortRef.current?.abort()
      const controller = new AbortController()
      pollAbortRef.current = controller

      pollForAccessToken({
        sessionId: session.sessionId,
        signal: controller.signal,
        onTransientError: (err) => {
          Sentry.addBreadcrumb({
            category: 'auth',
            message: 'Passwordless auth poll swallowed a transient error',
            level: 'warning',
            data: {
              component: sentryComponent,
              sessionId: session.sessionId,
              err: err instanceof Error ? err.message : String(err),
            },
          })
        },
      })
        .then((result) => {
          if (result.status === 'verified') {
            activeSessionRef.current = null
            Sentry.addBreadcrumb({
              category: 'auth',
              message: 'Passwordless auth verified',
              level: 'info',
              data: {
                component: sentryComponent,
                email: session.email,
                sessionId: session.sessionId,
              },
            })
            onVerified({
              email: session.email,
              accessToken: result.accessToken,
            })
            return
          }
          if (result.status === 'denied') {
            activeSessionRef.current = null
            Sentry.addBreadcrumb({
              category: 'auth',
              message: 'Passwordless auth denied',
              level: 'warning',
              data: {
                component: sentryComponent,
                email: session.email,
                sessionId: session.sessionId,
              },
            })
            setWaitingForApproval(false)
            setLoading(false)
            setDenied(true)
            return
          }
          if (controller.signal.aborted) return
          if (!activeSessionRef.current) return
          activeSessionRef.current = null
          const kind: PollErrorKind =
            result.status === 'timed-out' ? 'timed-out' : 'unexpected'
          setErrorMessage(
            kind === 'timed-out'
              ? errorMessages.timedOut
              : errorMessages.unexpected
          )
          setWaitingForApproval(false)
          setLoading(false)
        })
        .catch((err) => {
          if (controller.signal.aborted) return
          if (!activeSessionRef.current) return
          activeSessionRef.current = null
          Sentry.captureException(err, {
            tags: { component: sentryComponent, action: 'poll' },
            user: { email: session.email },
            extra: { sessionId: session.sessionId },
          })
          setErrorMessage(errorMessages.unexpected)
          setWaitingForApproval(false)
          setLoading(false)
        })
    },
    [sentryComponent, errorMessages, onVerified]
  )

  const beginPollRef = useRef(beginPoll)
  useEffect(() => {
    beginPollRef.current = beginPoll
  })

  useEffect(() => {
    const handler = () => {
      if (document.visibilityState === 'visible' && activeSessionRef.current) {
        beginPollRef.current(activeSessionRef.current)
      }
    }
    document.addEventListener('visibilitychange', handler)
    return () => document.removeEventListener('visibilitychange', handler)
  }, [])

  const sendMagicLink = useCallback(
    async ({ email, referral, vendor }: SendArgs) => {
      setLoading(true)
      setErrorMessage('')

      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'Starting passwordless auth',
        level: 'info',
        data: { component: sentryComponent, email },
      })

      try {
        const { sessionId } = await startPasswordlessLogin({
          email,
          client,
          referral,
          vendor,
        })
        try {
          window.clarity?.('set', 'paytrie_session_id', sessionId)
        } catch {
          //
        }
        const session = { sessionId, email }
        activeSessionRef.current = session
        setLastSendSucceeded(true)
        setWaitingForApproval(true)
        beginPoll(session)
      } catch (err) {
        setLastSendSucceeded(false)
        setErrorMessage(errorMessages.sendFailed)
        Sentry.captureException(err, {
          tags: {
            component: sentryComponent,
            action: 'startPasswordlessLogin',
          },
          user: { email },
        })
      } finally {
        setLoading(false)
      }
    },
    [beginPoll, client, errorMessages, sentryComponent]
  )

  const resetDenied = useCallback(() => {
    setDenied(false)
    setErrorMessage('')
  }, [])

  return {
    sendMagicLink,
    waitingForApproval,
    denied,
    loading,
    errorMessage,
    lastSendSucceeded,
    resetDenied,
  }
}
